/**
 * Pricing Structure Schema - Single Source of Truth
 * Defines the exact structure that must be used across:
 * - pricingConfig.js (DEFAULT_PRICES, DEFAULT_PROFITS)
 * - PricingRegistry.js (reset method)
 * - PricingRules.js (if needed)
 *
 * This ensures consistency and makes it easy to add/remove components
 */

export const PRICING_STRUCTURE = {
  doors: {
    acrylic: 'leaf',
    normal: {
      dulux: 'leaf',
    },
    alum: 'leaf',
    glassInside: 'leaf',
    noGrainPano: {
      dulux: 'leaf',
      melamine: 'leaf',
    },
    grainPano: {
      melamine: 'leaf',
    },
    louver: {
      dulux: 'leaf',
      melamine: 'leaf',
    },
    milled: {
      dulux: 'leaf',
      melamine: 'leaf',
    },
    drilled: {
      dulux: 'leaf',
      melamine: 'leaf',
    },
  },
  materials: {
    boards: 'leaf',
    acrylicCabinetTrims: 'leaf',
    edgeBanding: {
      normal: 'leaf',
      diff: 'leaf',
      bevel: 'leaf',
    },
  },
  component: {
    hinges: 'leaf',
    hinges135: 'leaf',
    connectors: 'leaf',
    rails: 'leaf',
    liftSupport: 'leaf',
  },
  accessory: {
    handles: 'leaf',
    leds: 'leaf',
    ledsDecor: 'leaf',
    lightSwitches: 'leaf',
    ledDrivers: 'leaf',
    clothesHangers: 'leaf',
    mirrors: 'leaf',
    legs: 'leaf',
    standardLock: 'leaf',
  },
  labor: {
    design: 'leaf',
    cnc: 'leaf',
    edgeBanding: {
      normal: 'leaf',
      diff: 'leaf',
      bevel: 'leaf',
    },
    packing: 'leaf',
    painting: 'leaf',
    assembly: {
      wardrobe: 'leaf',
      totl: 'leaf',
      dry: 'leaf',
      kitchenLower: 'leaf',
      kitchenUpper: 'leaf',
      kitchenCeiling: 'leaf',
      shoeCabinet: 'leaf',
      decorative: 'leaf',
      tv: 'leaf',
      bed: 'leaf',
      bedLower: 'leaf',
      bedDrawer: 'leaf',
      desk: 'leaf',
      vanity: 'leaf',
      shelf: 'leaf',
      shoeBench: 'leaf',
      wallPanelMelamine: 'leaf',
      slatWallPartition: 'leaf',
      wallPanelMirror: 'leaf',
      led: 'leaf',
      bedLower: 'leaf',
    },
  },
  special: {
    equipments: 'leaf',
    countertop: 'leaf',
    backsplash: 'leaf',
    curveBlocks: {
      'rank1.AC.M': 'leaf',
      'rank1.AC.L': 'leaf',
      'rank1.AC.Plastic': 'leaf',
      'rank1.KES.M': 'leaf',
      'rank1.KES.L': 'leaf',
      'rank1.ACC.Plywood': 'leaf',
      'rank2.AC.M': 'leaf',
      'rank2.AC.L': 'leaf',
      'rank2.AC.Plastic': 'leaf',
      'rank2.KES.M': 'leaf',
      'rank2.KES.L': 'leaf',
      'rank2.ACC.Plywood': 'leaf',
      'rank3.AC.M': 'leaf',
      'rank3.AC.L': 'leaf',
      'rank3.AC.Plastic': 'leaf',
      'rank3.KES.M': 'leaf',
      'rank3.KES.L': 'leaf',
      'rank3.ACC.Plywood': 'leaf',
    },
  },
  transport: {
    importDuty: 'leaf',
    shippingFee: 'leaf',
    groundTripFee: 'leaf',
  },
};

/**
 * Extract all leaf keys from structure for validation
 * Returns flat array of all component paths
 * @returns {string[]} Array of all keys
 */
export function getAllComponentKeys() {
  const keys = [];

  const traverse = (obj, prefix = '') => {
    for (const key in obj) {
      const value = obj[key];
      const fullKey = prefix ? `${prefix}.${key}` : key;

      if (value === 'leaf') {
        keys.push(fullKey);
      } else if (typeof value === 'object') {
        traverse(value, fullKey);
      }
    }
  };

  traverse(PRICING_STRUCTURE);
  return keys;
}

/**
 * Generate template registry from structure
 * Use for initializing PricingRegistry.reset()
 * @param {any} leafValue - Value to use for leaf nodes (e.g., [], 0, null)
 * @returns {object} Registry template with same structure
 */
export function generateRegistryTemplate(leafValue = []) {
  const template = {};

  const traverse = (source, target) => {
    for (const key in source) {
      const value = source[key];

      if (value === 'leaf') {
        target[key] = leafValue instanceof Array ? [...leafValue] : leafValue;
      } else if (typeof value === 'object') {
        target[key] = {};
        traverse(value, target[key]);
      }
    }
  };

  traverse(PRICING_STRUCTURE, template);
  return template;
}

/**
 * Generate template prices/profits from structure
 * Use for initializing pricingConfig defaults
 * @param {any} leafValue - Value to use for leaf nodes (e.g., 0, null, 0.4)
 * @returns {object} Config template with same structure
 */
export function generateConfigTemplate(leafValue = 0) {
  return generateRegistryTemplate(leafValue);
}

/**
 * Validate that object follows pricing structure
 * Ensures no extra keys and all required keys present
 * @param {object} obj - Object to validate
 * @param {string} context - Context name for error messages
 * @throws {Error} If structure doesn't match
 * @returns {boolean} True if valid
 */
export function validateStructure(obj, context = 'pricing') {
  const structureKeys = new Set(getAllComponentKeys());

  const traverse = (source, target, sourcePath = '') => {
    for (const key in target) {
      const fullPath = sourcePath ? `${sourcePath}.${key}` : key;

      if (!(key in source)) {
        console.warn(
          `⚠️  ${context}: Extra key found: ${fullPath} (not in PRICING_STRUCTURE)`
        );
      }

      const sourceValue = source[key];
      const targetValue = target[key];

      if (sourceValue === 'leaf') {
        // Leaf node - OK to have any value
        continue;
      } else if (typeof sourceValue === 'object' && sourceValue !== null) {
        if (typeof targetValue !== 'object' || targetValue === null) {
          throw new Error(
            `${context}: Expected object at ${fullPath}, got ${typeof targetValue}`
          );
        }
        traverse(sourceValue, targetValue, fullPath);
      }
    }

    // Check for missing required keys
    for (const key in source) {
      if (!(key in target)) {
        throw new Error(`${context}: Missing required key: ${key}`);
      }
    }
  };

  traverse(PRICING_STRUCTURE, obj);
  return true;
}

/**
 * Get all category names
 * @returns {string[]} ['doors', 'materials', 'component', 'accessory', 'labor', 'special']
 */
export function getCategories() {
  return Object.keys(PRICING_STRUCTURE);
}

/**
 * Get all types for a category
 * @param {string} category - Category name
 * @returns {string[]} Array of type names
 */
export function getTypesForCategory(category) {
  return Object.keys(PRICING_STRUCTURE[category] || {});
}

/**
 * Get all subtypes for a category/type
 * @param {string} category - Category name
 * @param {string} type - Type name
 * @returns {string[]|null} Array of subtype names or null if no subtypes
 */
export function getSubtypesForType(category, type) {
  const typeValue = PRICING_STRUCTURE[category]?.[type];

  if (typeValue === 'leaf') return null;
  if (typeof typeValue === 'object') {
    return Object.keys(typeValue).filter(k => typeValue[k] === 'leaf');
  }
  return null;
}

export default {
  PRICING_STRUCTURE,
  getAllComponentKeys,
  generateRegistryTemplate,
  generateConfigTemplate,
  validateStructure,
  getCategories,
  getTypesForCategory,
  getSubtypesForType,
};
